export interface ClawWarsE2EConfig {
  state?: "onboarding" | "landing" | "ios_intro" | "dashboard";
  authBypass?: boolean;
  currentUser?: {
    user: any;
    nation: any;
    citizenRecord: any;
    balance: any;
  };
}

const STORAGE_KEY = "cw_e2e_config";

declare global {
  interface Window {
    __CLAWWARS_E2E__?: ClawWarsE2EConfig;
  }
}

const fallbackCurrentUser = {
  user: {
    id: 0,
    username: "E2E Commander",
    displayName: "E2E Commander",
    walletAddress: null,
  },
  nation: null,
  citizenRecord: null,
  balance: null,
};

const dashboardNations = [
  {
    id: 1,
    countryCode: "USA",
    countryName: "United States",
    flagEmoji: "🇺🇸",
    totalCitizens: 128,
    totalStaked: "1250000",
    warChestBalance: "640000",
    bondingCurveReserve: "310000",
    coinPrice: "2.84",
    coinPriceDelta: 0.082,
    marketCap: "5840000",
    totalLiquidityCaptured: "920000",
    totalLiquidityLost: "280000",
    defenseMultiplier: "1.3",
    shieldExpiresAt: null,
    status: "active",
    warsWon: 9,
    warsLost: 3,
  },
  {
    id: 2,
    countryCode: "CHN",
    countryName: "China",
    flagEmoji: "🇨🇳",
    totalCitizens: 122,
    totalStaked: "1195000",
    warChestBalance: "590000",
    bondingCurveReserve: "295000",
    coinPrice: "2.51",
    coinPriceDelta: -0.024,
    marketCap: "5430000",
    totalLiquidityCaptured: "810000",
    totalLiquidityLost: "360000",
    defenseMultiplier: "1.1",
    shieldExpiresAt: null,
    status: "active",
    warsWon: 7,
    warsLost: 4,
  },
  {
    id: 3,
    countryCode: "ISR",
    countryName: "Israel",
    flagEmoji: "🇮🇱",
    totalCitizens: 74,
    totalStaked: "880000",
    warChestBalance: "420000",
    bondingCurveReserve: "210000",
    coinPrice: "3.27",
    coinPriceDelta: 0.051,
    marketCap: "3180000",
    totalLiquidityCaptured: "640000",
    totalLiquidityLost: "190000",
    defenseMultiplier: "1.4",
    shieldExpiresAt: null,
    status: "active",
    warsWon: 6,
    warsLost: 3,
  },
];

const dashboardWars = [
  {
    id: 401,
    warType: "strike",
    status: "active",
    attacker: { countryCode: "USA", flagEmoji: "🇺🇸" },
    defender: { countryCode: "CHN", flagEmoji: "🇨🇳" },
    totalCommitted: "320000",
  },
];

const dashboardWarFeed = [
  {
    id: 9001,
    eventType: "strike_executed",
    headline: "USA coalition executed a treasury strike against CHN.",
    createdAt: "2026-03-18T15:00:00.000Z",
  },
  {
    id: 9002,
    eventType: "citizen_joined",
    headline: "Three new citizens pledged allegiance to USA.",
    createdAt: "2026-03-18T14:57:00.000Z",
  },
];

const dashboardSimStates = {
  USA: {
    morale: 82,
    power: 1480,
    population: 920000,
    totalMilitary: 560,
    supplyStatus: "supplied",
    activeResearch: "orbital_shielding",
    researchTicksRemaining: 14,
    buildings: {
      commandCenter: { count: 3 },
      refinery: { count: 7 },
      barracks: { count: 6 },
      laboratory: { count: 4 },
    },
    military: {
      infantry: 320,
      armor: 82,
      drones: 54,
      artillery: 41,
    },
    research: {
      orbital_shielding: 0.72,
      missile_guidance: 1,
      autonomous_drones: 1,
      electronic_warfare: 0.45,
    },
    resources: {
      steel: 9200,
      fuel: 6100,
      intel: 3400,
      gold: 4800,
    },
    tradeRoutes: [
      { partnerCode: "ISR", goldPerTick: 32, active: true },
      { partnerCode: "CHN", goldPerTick: 18, active: false },
    ],
  },
  CHN: {
    morale: 74,
    power: 1390,
    population: 970000,
    totalMilitary: 590,
    supplyStatus: "strained",
    activeResearch: "missile_guidance",
    researchTicksRemaining: 22,
    buildings: {
      commandCenter: { count: 4 },
      refinery: { count: 8 },
      barracks: { count: 5 },
      laboratory: { count: 5 },
    },
    military: {
      infantry: 360,
      armor: 96,
      drones: 61,
      artillery: 38,
    },
    research: {
      orbital_shielding: 0.34,
      missile_guidance: 0.81,
      autonomous_drones: 0.52,
      electronic_warfare: 0.64,
    },
    resources: {
      steel: 10100,
      fuel: 5200,
      intel: 2800,
      gold: 5300,
    },
    tradeRoutes: [{ partnerCode: "USA", goldPerTick: 21, active: false }],
  },
  ISR: {
    morale: 88,
    power: 1040,
    population: 510000,
    totalMilitary: 390,
    supplyStatus: "supplied",
    activeResearch: "electronic_warfare",
    researchTicksRemaining: 9,
    buildings: {
      commandCenter: { count: 2 },
      refinery: { count: 5 },
      barracks: { count: 4 },
      laboratory: { count: 4 },
    },
    military: {
      infantry: 210,
      armor: 54,
      drones: 73,
      artillery: 27,
    },
    research: {
      orbital_shielding: 0.58,
      missile_guidance: 0.66,
      autonomous_drones: 0.74,
      electronic_warfare: 0.92,
    },
    resources: {
      steel: 6200,
      fuel: 4700,
      intel: 3900,
      gold: 3500,
    },
    tradeRoutes: [{ partnerCode: "USA", goldPerTick: 27, active: true }],
  },
};

const dashboardNationCitizens = {
  USA: [
    {
      id: 101,
      user: { displayName: "E2E Commander", walletAddress: null, elo: 1520 },
      stakedAmount: "250000",
      combatPower: "340",
      kills: 19,
      deaths: 4,
      rank: "general",
      isMercenary: false,
    },
    {
      id: 102,
      user: { displayName: "Signal Admiral", walletAddress: null, elo: 1410 },
      stakedAmount: "175000",
      combatPower: "220",
      kills: 11,
      deaths: 6,
      rank: "colonel",
      isMercenary: false,
    },
  ],
  CHN: [],
  ISR: [],
};

const dashboardNationHistory = {
  USA: [
    {
      id: 1,
      headline: "Treasury captured 92k CLAW from the CHN strike corridor.",
      createdAt: "2026-03-18T15:08:00.000Z",
    },
    {
      id: 2,
      headline: "Orbital shielding funding round cleared for deployment.",
      createdAt: "2026-03-18T14:41:00.000Z",
    },
  ],
  CHN: [],
  ISR: [],
};

const dashboardNationChart = {
  USA: [
    { timestamp: "2026-03-18T10:00:00.000Z", price: 2.12, volume: 1020 },
    { timestamp: "2026-03-18T11:00:00.000Z", price: 2.25, volume: 1280 },
    { timestamp: "2026-03-18T12:00:00.000Z", price: 2.33, volume: 1530 },
    { timestamp: "2026-03-18T13:00:00.000Z", price: 2.47, volume: 1710 },
    { timestamp: "2026-03-18T14:00:00.000Z", price: 2.61, volume: 1880 },
    { timestamp: "2026-03-18T15:00:00.000Z", price: 2.84, volume: 2140 },
  ],
  CHN: [],
  ISR: [],
};

export function getE2EConfig(): ClawWarsE2EConfig | null {
  if (typeof window === "undefined") return null;

  if (window.__CLAWWARS_E2E__) {
    return window.__CLAWWARS_E2E__;
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isE2EAuthBypassEnabled(): boolean {
  return !!getE2EConfig()?.authBypass;
}

export function getE2ECurrentUser() {
  return getE2EConfig()?.currentUser || fallbackCurrentUser;
}

export function isE2EDashboardState(): boolean {
  const state = getE2EConfig()?.state;
  return state === "dashboard" || state === "ios_intro" || state === "onboarding";
}

export function getE2EDashboardData() {
  return {
    nations: dashboardNations,
    leaderboard: dashboardNations,
    wars: dashboardWars,
    warFeed: dashboardWarFeed,
    simStates: dashboardSimStates,
    nationCitizens: dashboardNationCitizens,
    nationHistory: dashboardNationHistory,
    nationChart: dashboardNationChart,
  };
}
